import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { http } from "@/lib/api";
import MilestoneModal from "@/components/MilestoneModal";
import { Sparkles, X } from "lucide-react";

/**
 * Non-blocking banner that surfaces due milestones. Fetches /beta/milestones
 * on mount + when auth changes. Handles local 24h dismiss (per-milestone
 * localStorage key). If the milestone has hit its `resurface_day`, the local
 * dismiss is ignored so the user is nudged again.
 */
const LABELS = {
  day7_checkin:  { title: "Quick Day 7 check-in", body: "How's your first week going? Takes 30 seconds." },
  day30_product: { title: "Day 30 product feedback", body: "You've had a month with Discrimina. Help shape what's next." },
  final_pmf:     { title: "Final beta survey", body: "You made it! Two minutes to help us decide what stays and what ships next." },
};

const dismissKey = (userId, name) => `discrimina_milestone_dismiss_${userId}_${name}`;

export default function MilestoneBanner() {
  const { user, refresh } = useAuth();
  const [due, setDue] = useState([]);
  const [open, setOpen] = useState(null); // milestone name or null

  useEffect(() => {
    let cancelled = false;
    async function load() {
      if (!user?.beta_access_type) return;
      try {
        const { data } = await http.get("/beta/milestones");
        if (cancelled) return;
        setDue(data?.due || []);
      } catch { /* silent */ }
    }
    load();
    return () => { cancelled = true; };
  }, [user?.id, user?.beta_access_type, user?.feedback_milestones_completed?.length]);

  const visible = due.filter((m) => {
    if (!user) return false;
    const dismissedAt = parseInt(localStorage.getItem(dismissKey(user.id, m.name)) || "0", 10);
    // If we've hit the resurface day, ignore the local dismiss.
    if (m.resurface_day && m.days_in_beta >= m.resurface_day) return true;
    if (!dismissedAt) return true;
    return Date.now() - dismissedAt > 24 * 60 * 60 * 1000;
  });

  if (!visible.length) {
    return open ? <MilestoneModal name={open} onClose={() => { setOpen(null); refresh?.(); }} /> : null;
  }
  const m = visible[0]; // one at a time, in due-order
  const label = LABELS[m.name] || { title: m.name, body: "" };

  const dismiss = () => {
    localStorage.setItem(dismissKey(user.id, m.name), String(Date.now()));
    // Also log server-side (non-blocking).
    http.post(`/beta/milestone/${m.name}/dismiss`).catch(() => {});
    setDue((prev) => prev.filter((x) => x.name !== m.name));
  };

  return (
    <>
      <div
        className="mx-4 md:mx-8 mt-4 rounded-2xl border border-teal-200 bg-gradient-to-r from-teal-50 to-white p-4 flex items-center gap-4 shadow-sm"
        data-testid={`milestone-banner-${m.name}`}
      >
        <div className="hidden sm:flex w-10 h-10 rounded-xl bg-teal-600 text-white items-center justify-center shrink-0">
          <Sparkles className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-slate-900 text-sm">{label.title}</div>
          <div className="text-xs text-slate-600 mt-0.5">{label.body}</div>
        </div>
        <button
          onClick={() => setOpen(m.name)}
          data-testid={`milestone-open-${m.name}`}
          className="h-9 px-4 rounded-full bg-teal-600 hover:bg-teal-700 text-white text-xs font-semibold shrink-0"
        >
          Take the survey
        </button>
        <button
          onClick={dismiss}
          aria-label="Dismiss for today"
          data-testid={`milestone-dismiss-${m.name}`}
          className="text-slate-400 hover:text-slate-700 shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      {open && <MilestoneModal name={open} onClose={() => { setOpen(null); refresh?.(); }} />}
    </>
  );
}
